
import { useState, useEffect } from 'react';
import { checkPendingReferral } from '@/services/sharing/referralService';
import { PendingReferralResult } from '@/types/referral';
import { usePermissions } from '@/contexts/unified-permissions';
import { ReferralStorageService } from '@/services/referral/ReferralStorageService';

export interface ReferralStatus {
  isApplied: boolean;
  discountPercentage: number | null;
  code: string | null;
  description: string | null;
  source?: string;
}

export function useReferralStatus(subscriptionType: 'monthly' | 'annual' = 'monthly') {
  const [status, setStatus] = useState<ReferralStatus>({
    isApplied: false,
    discountPercentage: null,
    code: null,
    description: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = usePermissions();

  // Check for stored referral code
  useEffect(() => {
    const checkReferral = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // First check for a referral code using our storage service
        const storedCode = ReferralStorageService.getStoredReferralCode();
        
        if (storedCode) {

          setStatus({
            isApplied: true,
            discountPercentage: subscriptionType === 'annual' ? 60 : 30,
            code: storedCode,
            description: `${subscriptionType === 'annual' ? '60%' : '30%'} off first payment with referral`,
            source: 'Friend referral'
          });
        } 
        // If no stored code but user is logged in, check for pending referrals
        else if (user?.email) {

          const pendingReferral: PendingReferralResult = await checkPendingReferral(
            user.email,
            subscriptionType
          );
          
          if (pendingReferral.found && pendingReferral.referral_code) {

            setStatus({
              isApplied: true,
              discountPercentage: pendingReferral.promotion_amount || (subscriptionType === 'annual' ? 60 : 30),
              code: pendingReferral.referral_code,
              description: pendingReferral.promotion_description || `${subscriptionType === 'annual' ? '60%' : '30%'} off first payment`,
              source: pendingReferral.campaign_source || 'Referral'
            });
          } else {

            setStatus({
              isApplied: false,
              discountPercentage: null,
              code: null,
              description: null
            });
          }
        } else {

          setStatus({
            isApplied: false,
            discountPercentage: null,
            code: null,
            description: null
          });
        }
      } catch (error: unknown) {
        console.error("Error checking referral status:", error);
        setError(error instanceof Error ? error.message : 'Failed to load referral code');
        
        // Set fallback values to avoid UI breaking
        setStatus({
          isApplied: false,
          discountPercentage: null,
          code: null,
          description: null
        });
      } finally {
        setLoading(false);
      }
    };
    
    checkReferral();
  }, [user, subscriptionType]);
  
  return {
    ...status,
    loading,
    error
  };
}
